import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { TextField } from '@material-ui/core';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import toastr from 'toastr';
import BannerMetadata from '../../enhanced_ads/BannerMetadata/BannerMetadata';
import materialTheme from '../../reactMaterialTheme';
import HeadlineField from '../HeadlineField/HeadlineField';
import ThumbnailField from '../ImageUpload/ThumbnailField';
import BrandLogoField from '../ImageUpload/BrandLogoField';
import {
  useCreativeInput,
  useCurrentUser,
  useNgCreativeService,
  UserProvider
} from '../../hooks';
import http from '../../shared/http';

import 'react-tabs/style/react-tabs.css';

const EnhancedDisplayBuilder = props => {
  return (
    <MuiThemeProvider theme={materialTheme}>
      <UserProvider>
        <EnhancedDisplayForm {...props} />
      </UserProvider>
    </MuiThemeProvider>
  );
};

const EnhancedDisplayForm = props => {
  const [creative, setCreative] = useState(
    props.creative.id
      ? props.creative
      : {
          name: '',
          action: 'banner',
          headline: 'Click Here for More Information',
          description: "Don't miss this content from our sponsor",
          advertiser: 'Sponsor',
          ...props.creative
        }
  );

  const [activeTabIndex, setActiveTabIndex] = useState(
    creative.thumbnailUrl ? 2 : creative.moatUrl ? 1 : 0
  );

  const creativeInputProps = useCreativeInput(
    [
      'adm',
      'advertiser',
      'headline',
      'description',
      'name',
      'moatUrl',
      'mediaUrl',
      'thumbnailUrl',
      'brandLogoUrl'
    ],
    creative,
    setCreative
  );

  useNgCreativeService(creative, props.CreativeService, props.RenderService);

  useEffect(
    function insertThumbnailUrlIntoAdm() {
      if (creative.thumbnailUrl) {
        const adm = `<div className="str-thumbnail" style="height: 100%; background-image: url('${creative.thumbnailUrl}'); background-repeat: no-repeat; background-color: transparent; background-size: contain" />`;

        setCreative({ ...creative, adm });
      }
    },
    [creative.thumbnailUrl]
  );

  useEffect(
    function handleMoatChange() {
      const { moatUrl } = creative;
      const matches =
        moatUrl &&
        moatUrl.match(/https?:\/\/moat\.com\/creative\/(.*?)(\?.*)?$/);

      if (matches) {
        const id = matches[1];
        http
          .post('/api/internal/moat_creative/', { id })
          .then(({ data }) => {
            setCreative(currentCreative => ({
              ...currentCreative,
              adm: data.adm
            }));
          })
          .catch(() => {
            toastr.error(`Failed to resolve image at ${moatUrl}`);
          });
      }
    },
    [creative.moatUrl]
  );

  const handleTabSelection = tabIndex => {
    setActiveTabIndex(tabIndex);
    setCreative({
      ...creative,
      moatUrl: undefined,
      thumbnailUrl: undefined,
      adm: undefined
    });
  };

  const currentUser = useCurrentUser();

  return (
    <form className="enhanced-ad_form" autoComplete="off">
      <div className="row mb2">
        <TextField
          {...creativeInputProps.nameProps}
          label={
            <label htmlFor={creativeInputProps.nameProps.id}>Ad Name</label>
          }
          className="col-xs-10"
          onFocus={event => event.target.select()}
          required
        />
      </div>
      <div className="row mt2 mb2">
        <TextField
          {...creativeInputProps.advertiserProps}
          label={
            <label htmlFor={creativeInputProps.advertiserProps.id}>
              Brand Name
            </label>
          }
          className="col-xs-10"
          onFocus={event => event.target.select()}
          required
        />
      </div>
      <div className="row mt2 mb2">
        <BrandLogoField
          logoUrl={creativeInputProps.brandLogoUrlProps.value}
          onLogoUrlChange={creativeInputProps.brandLogoUrlProps.onChange}
        />
      </div>
      <div className="row mt2 mb2">
        <div className="col-xs-10 p0">
          <HeadlineField
            headline={creativeInputProps.headlineProps.value}
            onChange={creativeInputProps.headlineProps.onChange}
          />
        </div>
      </div>
      <div className="row mt2 mb2">
        <TextField
          {...creativeInputProps.descriptionProps}
          label={
            <label htmlFor={creativeInputProps.descriptionProps.id}>
              Description
            </label>
          }
          className="col-xs-10"
          onFocus={event => event.target.select()}
          required
        />
      </div>
      <div className="row mt2 mb2">
        <TextField
          {...creativeInputProps.mediaUrlProps}
          className="col-xs-10"
          label={
            <label htmlFor={creativeInputProps.mediaUrlProps.id}>
              Clickout URL
            </label>
          }
          onFocus={event => event.target.select()}
          required
        />
      </div>
      <div className="row mt4 mb2">
        <Tabs selectedIndex={activeTabIndex} onSelect={handleTabSelection}>
          <TabList>
            <Tab>Banner Tag</Tab>
            <Tab>Moat Share URL</Tab>
            <Tab>Image Upload</Tab>
          </TabList>

          <TabPanel>
            <div className="row">
              <div className="col-xs-10 mt2">
                <label htmlFor="adm" className="enhanced-ad_label">
                  Drop your JS tag below to preview
                </label>
                <textarea
                  {...creativeInputProps.admProps}
                  className="enhanced-ad_form-input"
                  id="adm"
                  rows="8"
                  onFocus={event => event.target.select()}
                />
              </div>
            </div>
          </TabPanel>
          <TabPanel>
            <TextField
              {...creativeInputProps.moatUrlProps}
              className="mt2 mb1 col-xs-10"
              label={
                <label htmlFor={creativeInputProps.moatUrlProps.id}>
                  Moat Share URL
                </label>
              }
              inputProps={{ 'data-testid': 'moat-url-input' }}
              onFocus={event => event.target.select()}
              required
            />
            <p className="help-text col-xs-10 ml0 pl0">
              Find a 300x250 banner of your choice at moat.com, click
              &quot;Share&quot;, then &quot;Copy Link&quot; and paste here
            </p>
          </TabPanel>
          <TabPanel>
            <div className="mt2">
              <ThumbnailField
                action="banner"
                onChange={creativeInputProps.thumbnailUrlProps.onChange}
                thumbnailUrl={creativeInputProps.thumbnailUrlProps.value}
              />
            </div>
          </TabPanel>
        </Tabs>
      </div>
      {creative.adm &&
        !creative.thumbnailUrl &&
        !creative.moatUrl &&
        currentUser &&
        currentUser.admin && (
          <div className="row">
            <BannerMetadata />
          </div>
        )}
    </form>
  );
};

EnhancedDisplayBuilder.propTypes = {
  CreativeService: PropTypes.object.isRequired,
  RenderService: PropTypes.object.isRequired,
  creative: PropTypes.object.isRequired
};
EnhancedDisplayForm.propTypes = {
  ...EnhancedDisplayBuilder.propTypes
};

export default EnhancedDisplayBuilder;
